import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiArchiveOutline, mdiBackupRestore } from '@mdi/js'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { useArchiveDocument, useRestoreDocument } from '../../hooks/use-archive'

/**
 * Archive / Restore on a document page (spec 2026-10-08-02). An archived
 * document leaves the library and is listed on the archive page.
 */
export function DocumentArchiveAction({
    org,
    docUid,
    archivedAt,
    onChanged,
    label: buttonLabel,
    testId,
}: {
    org: string
    docUid: string
    archivedAt?: string | null
    onChanged?: () => void
    /** A text button with this label instead of the icon button (the duplicates banner, spec 2026-11-03-04). */
    label?: string
    /** The archive button's data-testid, `document-archive-btn` by default. */
    testId?: string
}) {
    const { t } = useTranslation('archive')
    const archive = useArchiveDocument(org)
    const restore = useRestoreDocument(org)
    const [open, setOpen] = useState(false)

    if (archivedAt) {
        const label = t('actions.restore')
        return (
            <Button
                variant="outline"
                className="p-2"
                aria-label={label}
                title={label}
                disabled={restore.isPending}
                onClick={async () => {
                    await restore.mutateAsync(docUid)
                    onChanged?.()
                }}
                data-testid="document-restore-btn"
            >
                <AppIcon path={mdiBackupRestore} size="md" />
            </Button>
        )
    }

    const label = t('actions.archive')
    return (
        <>
            {buttonLabel ? (
                <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={testId ?? 'document-archive-btn'}>
                    <AppIcon path={mdiArchiveOutline} size="sm" />
                    {buttonLabel}
                </Button>
            ) : (
                <Button variant="outline" className="p-2" aria-label={label} title={label} onClick={() => setOpen(true)} data-testid={testId ?? 'document-archive-btn'}>
                    <AppIcon path={mdiArchiveOutline} size="md" />
                </Button>
            )}
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent data-testid="document-archive-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{label}</AlertDialogTitle>
                        <AlertDialogDescription>{t('confirm_archive_document')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="document-archive-cancel">{t('dialog.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="document-archive-confirm"
                            disabled={archive.isPending}
                            onClick={async (e) => {
                                e.preventDefault()
                                await archive.mutateAsync(docUid)
                                setOpen(false)
                                onChanged?.()
                            }}
                        >
                            {label}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}
